import { create } from 'zustand';

/**
 * Singleton lightbox state. Image bubbles call `open()` with the image
 * URL and the bounding rect of the source thumbnail in the page; the
 * mounted `<PhotoLightbox />` host reads from this store and renders
 * a fullscreen viewer with a FLIP entry animation that originates
 * from `srcRect`. Close clears `src` so the host unmounts the viewer.
 *
 * Kept as one global store (rather than per-page) so the host can
 * be mounted once in the app shell and any page (link, future
 * session, profile) can trigger it without prop-drilling.
 */
export interface LightboxSource {
  src: string;
  srcRect: DOMRect;
  // Natural dimensions of the full-size image (from the message
  // content). Used to fit the image in the viewport without waiting
  // for `<img onLoad>` to measure it. Fallback to source rect ratio
  // when unknown.
  naturalWidth?: number;
  naturalHeight?: number;
  // The thumbnail element clicked. The lightbox hides it (via
  // `visibility: hidden`) while the viewer is open so the source
  // doesn't show through the fading backdrop alongside the animating
  // copy — visible double-image is the most noticeable artifact of a
  // FLIP-style transition without it. Restored on unmount.
  srcEl?: HTMLElement;
}

interface LightboxState {
  current: LightboxSource | null;
  open: (s: LightboxSource) => void;
  close: () => void;
}

export const useLightbox = create<LightboxState>((set) => ({
  current: null,
  open:  (s) => set({ current: s }),
  close: () => set({ current: null }),
}));

export function openLightbox(source: LightboxSource) {
  useLightbox.getState().open(source);
}
